/**
 * Вишлист — целиком на клиенте.
 *
 * Хранение: localStorage, ключ italian-edit:wishlist, массив хендлов
 * товаров. Ни аккаунта, ни сервера: список живёт в браузере и НЕ
 * переезжает между устройствами. Синхронизация по аккаунту требует записи
 * в метаполя покупателя — это либо приложение, либо свой сервер.
 * Апгрейд возможен без переделки витрины: достаточно подменить read/write
 * на запросы к серверу — компоненты ниже их не видят.
 *
 * Почему храним хендл, а не id: страница вишлиста добирает карточки
 * через Section Rendering API по адресу /products/<handle>, id там не
 * годится. Плата за это — переименование товара роняет его из вишлиста;
 * такой хендл вычищается сам (см. fetchCard).
 */

const KEY = 'italian-edit:wishlist';
const CHANGE = 'wishlist:change';

/** Страховка от раздувшегося хранилища: вишлист в тысячу позиций — баг. */
const LIMIT = 200;

/**
 * Резерв на случай недоступного localStorage: приватный режим Safari
 * и переполненное хранилище бросают исключение на записи. Тогда
 * вишлист работает до перезагрузки страницы, а не отваливается молча.
 */
let memory = null;

function read() {
  if (memory) return [...memory];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((handle) => typeof handle === 'string' && handle.length > 0);
  } catch {
    memory = [];
    return [];
  }
}

function write(list) {
  const next = list.slice(0, LIMIT);
  if (memory) {
    memory = next;
  } else {
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      memory = next;
    }
  }
  document.dispatchEvent(new CustomEvent(CHANGE, { detail: { handles: next } }));
}

export const Wishlist = {
  CHANGE,
  handles: () => read(),
  count: () => read().length,
  has: (handle) => read().includes(handle),

  add(handle) {
    const list = read();
    if (list.includes(handle)) return;
    // Свежее сверху — так же строится сетка на странице вишлиста.
    list.unshift(handle);
    write(list);
  },

  remove(handle) {
    write(read().filter((item) => item !== handle));
  },

  toggle(handle) {
    const turningOn = !read().includes(handle);
    if (turningOn) this.add(handle);
    else this.remove(handle);
    return turningOn;
  },
};

/*
 * Две вкладки одного магазина держим в согласии. Событие storage
 * приходит только в ДРУГИЕ вкладки, так что эха и цикла здесь нет.
 */
window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  document.dispatchEvent(new CustomEvent(CHANGE, { detail: { handles: read() } }));
});

/** Сердце на карточке или кнопка на странице товара. */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.control = this.querySelector('button');
    if (!this.handle || !this.control) return;

    this.onClick = (event) => {
      /*
       * Кнопка лежит поверх ссылки-оверлея карточки (.product-card__link,
       * inset: 0) и внутри <product-card>, у которого свой обработчик
       * перехода (on:click="/handleViewTransition"). Без обеих остановок
       * клик по сердцу уводит на страницу товара.
       */
      event.preventDefault();
      event.stopPropagation();
      Wishlist.toggle(this.handle);
    };
    this.onChange = () => this.sync();

    this.control.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE, this.onChange);
    this.sync();
  }

  disconnectedCallback() {
    this.control?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE, this.onChange);
  }

  sync() {
    const saved = Wishlist.has(this.handle);
    this.control.setAttribute('aria-pressed', saved ? 'true' : 'false');

    const label = saved ? this.dataset.labelOn : this.dataset.labelOff;
    if (label) this.control.setAttribute('aria-label', label);

    const text = this.querySelector('[data-wishlist-label]');
    if (text && this.dataset.textOn && this.dataset.textOff) {
      text.textContent = saved ? this.dataset.textOn : this.dataset.textOff;
    }
  }
}

/** Счётчик у иконки в шапке. Пустой вишлист — без бейджа вовсе. */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.onChange = () => this.sync();
    document.addEventListener(CHANGE, this.onChange);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE, this.onChange);
  }

  sync() {
    const count = Wishlist.count();
    this.textContent = count > 99 ? '99+' : String(count);
    this.toggleAttribute('hidden', count === 0);
  }
}

/**
 * Стили, приехавшие вместе с карточкой, переносим в документ.
 *
 * В ответе section-rendering лежит <style data-section-stylesheet> на ~50 КБ —
 * это оформление галереи, бейджей, слайдшоу и быстрого добавления.
 * На самой странице вишлиста Liquid этих сниппетов не исполняется, так что
 * в её собственный CSS они не попадают.
 *
 * Без этого карточки приезжали неоформленными: бейдж «Sale» растягивался
 * в полосу во всю карточку, быстрое добавление вставало в поток, а стрелки
 * слайдшоу торчали в углу. Причина была именно в том, что fetchCard брал
 * из ответа только <li> и весь этот <style> выбрасывал.
 *
 * Дедуп обязателен: ответ одинаков для любого товара, и без него мы бы
 * вливали по 50 КБ CSS на каждую карточку.
 */
const adoptedStyles = new Set();

function adoptStyles(parsed, card) {
  for (const style of parsed.querySelectorAll('style')) {
    // Стили внутри карточки уедут вместе с ней — их трогать не надо.
    if (card && card.contains(style)) continue;

    const css = style.textContent;
    if (!css || !css.trim()) continue;

    const key = css.length + ':' + css.slice(0, 200);
    if (adoptedStyles.has(key)) continue;
    adoptedStyles.add(key);

    const clone = document.createElement('style');
    clone.setAttribute('data-wishlist-adopted', '');
    clone.textContent = css;
    document.head.append(clone);
  }
}

/**
 * Сетка на странице вишлиста.
 *
 * Сама страница отдаётся пустой и кешируется CDN как обычно; содержимое
 * добирается поштучно через Section Rendering API. Складывать все товары
 * в разметку и прятать лишние нельзя: сегодня это 90 карточек на каждый
 * просмотр, а на полном каталоге BrandsGateway — десятки тысяч.
 */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector('[data-wishlist-items]');
    this.sectionId = this.dataset.cardSection;
    this.root = (this.dataset.root || '/').replace(/\/$/, '');
    if (!this.items || !this.sectionId) return;

    this.onChange = () => this.render();
    document.addEventListener(CHANGE, this.onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE, this.onChange);
  }

  /*
   * Перерисовка асинхронная, а события могут прийти во время загрузки —
   * в том числе от самой себя, когда fetchCard выбрасывает битый хендл.
   * Без этого замка получаем гонку и двойные карточки.
   */
  async render() {
    if (this.rendering) {
      this.queued = true;
      return;
    }
    this.rendering = true;
    try {
      await this.paint();
    } finally {
      this.rendering = false;
      if (this.queued) {
        this.queued = false;
        this.render();
      }
    }
  }

  async paint() {
    const handles = Wishlist.handles();

    for (const node of [...this.items.children]) {
      if (!handles.includes(node.dataset.handle)) node.remove();
    }

    const present = new Set([...this.items.children].map((node) => node.dataset.handle));
    const missing = handles.filter((handle) => !present.has(handle));

    if (missing.length > 0) {
      this.dataset.state = 'loading';
      const cards = await Promise.all(missing.map((handle) => this.fetchCard(handle)));
      for (const card of cards) {
        if (card) this.items.append(card);
      }
    }

    // Порядок в DOM приводим к порядку в хранилище: append перемещает узел.
    for (const handle of handles) {
      const node = this.items.querySelector(`[data-handle="${CSS.escape(handle)}"]`);
      if (node) this.items.append(node);
    }

    this.dataset.state = this.items.children.length > 0 ? 'ready' : 'empty';
  }

  async fetchCard(handle) {
    const url = `${this.root}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.sectionId)}`;
    try {
      const response = await fetch(url);

      // 404 проверяем на всякий случай, но сегодня сюда не попадаем — см. ниже.
      if (response.status === 404) {
        Wishlist.remove(handle);
        return null;
      }
      if (!response.ok) return null;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = parsed.querySelector('[data-wishlist-card]');

      if (!card) {
        /*
         * Битый хендл Shopify отдаёт НЕ 404: на /products/<нет-такого>
         * с section_id приходит 200 и пустая обёртка секции (всего ~74
         * байта). Значит Liquid отработал, а product оказался пуст —
         * товара больше нет, чистим. Судить по коду ответа здесь
         * нельзя — это проверено на живом магазине.
         */
        Wishlist.remove(handle);
        return null;
      }

      // Строго ДО вставки карточки, иначе она мигнёт неоформленной.
      adoptStyles(parsed, card);

      return card;
    } catch {
      // Сеть отвалилась — хендл НЕ трогаем, повторим на следующем заходе.
      return null;
    }
  }
}

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('wishlist-button', WishlistButton);
define('wishlist-count', WishlistCount);
define('wishlist-grid', WishlistGrid);
